import React, { useState, useEffect } from 'react';
import { 
  Plus,
  DollarSign,
  Building2,
  User,
  MoreVertical,
  Edit,
  Trash2,
  Settings,
  Palette,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Check,
  X,
  Trophy,
  Ban,
  Eye,
  MessageSquare,
  Phone,
  Mail,
  Globe,
  MapPin,
  Briefcase,
  Tag,
  CheckSquare,
  Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import EmpresaDetailModal from './EmpresaDetailModal';
import EmpresaSearchSelect from './EmpresaSearchSelect';
import * as api from '../services/api';
import { formatDateTimePR } from '../utils/dateUtils';

const EmbudosPage = ({ currentUser, canDelete = () => true, globalSearch = '' }) => {
  const [pipelines, setPipelines] = useState([]);
  const [deals, setDeals] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [userFilter, setUserFilter] = useState('mine'); // Default to current user's deals
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const [showAddPipelineModal, setShowAddPipelineModal] = useState(false);
  const [showEditPipelineModal, setShowEditPipelineModal] = useState(false);
  const [showEditStagesModal, setShowEditStagesModal] = useState(false);
  const [showDealDetailModal, setShowDealDetailModal] = useState(false);
  const [showEmpresaModal, setShowEmpresaModal] = useState(false);
  const [showEditDealModal, setShowEditDealModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [selectedEmpresaIdForModal, setSelectedEmpresaIdForModal] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedDealEmpresa, setSelectedDealEmpresa] = useState(null);
  const [editingDealValue, setEditingDealValue] = useState(null);
  const [editingDealMonthlyValue, setEditingDealMonthlyValue] = useState(null);
  const [editingDealId, setEditingDealId] = useState(null);
  const [detailModalTab, setDetailModalTab] = useState('info');
  const [selectedStage, setSelectedStage] = useState('');
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [editingStages, setEditingStages] = useState([]);

  const isAdmin = currentUser?.role === 'administrator' || currentUser?.role === 'admin';

  const [newDeal, setNewDeal] = useState({
    empresa_id: '',
    empresa_name: '',
    empresa_category: 'Empresa',
    value: '',
    monthly_value: '',
    probability: 20,
    expected_close_date: '',
    assigned_to: '',
    notes: ''
  });
  const [newPipeline, setNewPipeline] = useState({
    name: '',
    description: '',
    stages: [
      { name: 'Lead', color: '#93C5FD', order: 0, is_won_stage: false, is_lost_stage: false },
      { name: 'Contactado', color: '#3B82F6', order: 1, is_won_stage: false, is_lost_stage: false },
      { name: 'Propuesta', color: '#6B7280', order: 2, is_won_stage: false, is_lost_stage: false },
      { name: 'Cerrado Ganado', color: '#22C55E', order: 3, is_won_stage: true, is_lost_stage: false },
      { name: 'Cerrado Perdido', color: '#EF4444', order: 4, is_won_stage: false, is_lost_stage: true }
    ],
    color: '#D4A843'
  });

  const colorOptions = [
    '#D4A843', '#EF4444', '#F97316', '#EAB308', '#22C55E',
    '#14B8A6', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899'
  ];

  const stageColorOptions = [
    '#93C5FD', '#3B82F6', '#1D4ED8', // Blues
    '#86EFAC', '#22C55E', '#15803D', // Greens
    '#FDE047', '#EAB308', '#CA8A04', // Yellows
    '#FDBA74', '#F97316', '#EA580C', // Oranges
    '#FCA5A5', '#EF4444', '#DC2626', // Reds
    '#D8B4FE', '#A855F7', '#7C3AED', // Purples
    '#F9A8D4', '#EC4899', '#DB2777', // Pinks
    '#9CA3AF', '#6B7280', '#374151', // Grays
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (pipelines.length > 0 && !selectedPipeline) {
      setSelectedPipeline(pipelines[0]);
    }
  }, [pipelines]);

  useEffect(() => {
    if (selectedPipeline) {
      loadDeals(selectedPipeline.id);
    }
  }, [selectedPipeline]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pipelinesRes, empresasRes, usersRes] = await Promise.all([
        api.getPipelines(),
        api.getEmpresas(),
        api.getUsers()
      ]);
      setPipelines(pipelinesRes.data);
      setEmpresas(empresasRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDeals = async (pipelineId) => {
    try {
      const response = await api.getDeals();
      const filteredDeals = response.data.filter(d => d.pipeline_id === pipelineId);
      setDeals(filteredDeals);
    } catch (error) {
      console.error('Error loading deals:', error);
    }
  };

  const getStages = () => {
    if (!selectedPipeline) return [];
    return selectedPipeline.stages || [];
  };

  const getDealsByStage = (stageName) => {
    return deals.filter(d => {
      if (d.stage_name !== stageName) return false;
      // Global search filter
      if (globalSearch) {
        const searchLower = globalSearch.toLowerCase();
        const matchesSearch = 
          d.name?.toLowerCase().includes(searchLower) ||
          d.empresa_name?.toLowerCase().includes(searchLower) ||
          d.assigned_to?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      // Apply user filter
      if (userFilter === 'mine') {
        return d.assigned_to === currentUser?.name;
      } else if (userFilter !== 'all') {
        return d.assigned_to === userFilter;
      }
      return true;
    });
  };

  const getStageValue = (stageName) => {
    return getDealsByStage(stageName).reduce((sum, d) => sum + (d.value || 0), 0);
  };

  // Get filtered deals based on user filter
  const getFilteredDeals = () => {
    return deals.filter(d => {
      // Global search filter
      if (globalSearch) {
        const searchLower = globalSearch.toLowerCase();
        const matchesSearch = 
          d.name?.toLowerCase().includes(searchLower) ||
          d.empresa_name?.toLowerCase().includes(searchLower) ||
          d.assigned_to?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      if (userFilter === 'mine') {
        return d.assigned_to === currentUser?.name;
      } else if (userFilter !== 'all') {
        return d.assigned_to === userFilter;
      }
      return true;
    });
  };

  // Calculate potential commission for filtered deals (35%)
  const getMyPotentialCommission = () => {
    const filteredDeals = getFilteredDeals();
    const activeDealValue = filteredDeals
      .filter(d => !d.is_won && !d.is_lost)
      .reduce((sum, d) => sum + (d.value || 0), 0);
    return activeDealValue * 0.35;
  };

  // Drag and Drop handlers
  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', deal.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, stage) => {
    e.preventDefault();
    if (!draggedDeal) return;
    
    if (draggedDeal.stage_name === stage.name) {
      setDraggedDeal(null);
      return;
    }

    try {
      // Update deal via API
      await api.moveDeal(draggedDeal.id, stage.id || stage.name, stage.name);
      
      // Update local state
      setDeals(deals.map(d => {
        if (d.id === draggedDeal.id) {
          return {
            ...d,
            stage_id: stage.id || stage.name,
            stage_name: stage.name,
            is_won: stage.is_won_stage || false,
            is_lost: stage.is_lost_stage || false
          };
        }
        return d;
      }));
    } catch (error) {
      console.error('Error moving deal:', error);
    }
    
    setDraggedDeal(null);
  };

  const handleAddDeal = async () => {
    if (!selectedPipeline || !selectedStage) return;
    
    const empresa = empresas.find(e => e.id === newDeal.empresa_id);
    const stage = getStages().find(s => s.name === selectedStage);
    
    try {
      const dealData = {
        empresa_id: newDeal.empresa_id,
        empresa_name: empresa?.name || newDeal.empresa_name,
        empresa_category: empresa?.category || 'Empresa',
        pipeline_id: selectedPipeline.id,
        stage_id: stage?.id || selectedStage,
        stage_name: selectedStage,
        value: parseFloat(newDeal.value) || 0,
        monthly_value: parseFloat(newDeal.monthly_value) || 0,
        probability: newDeal.probability,
        expected_close_date: newDeal.expected_close_date,
        assigned_to: newDeal.assigned_to || currentUser?.name || '',
        notes: newDeal.notes,
        is_won: stage?.is_won_stage || false,
        is_lost: stage?.is_lost_stage || false
      };
      
      const response = await api.createDeal(dealData);
      setDeals([...deals, response.data]);
      setShowAddDealModal(false);
      setNewDeal({ empresa_id: '', empresa_name: '', empresa_category: 'Empresa', value: '', monthly_value: '', probability: 20, expected_close_date: '', assigned_to: '', notes: '' });
      setSelectedStage('');
    } catch (error) {
      console.error('Error creating deal:', error);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (window.confirm('¿Está seguro de eliminar este deal?')) {
      try {
        await api.deleteDeal(dealId);
        setDeals(deals.filter(d => d.id !== dealId));
      } catch (error) {
        console.error('Error deleting deal:', error);
      }
    }
  };

  const handleToggleWon = async (deal) => {
    try {
      const response = await api.updateDeal(deal.id, { is_won: !deal.is_won, is_lost: false });
      setDeals(deals.map(d => d.id === deal.id ? response.data : d));
    } catch (error) {
      console.error('Error updating deal:', error);
    }
  };

  const handleOpenEditDeal = (deal) => {
    setEditingDeal({
      ...deal,
      stage_id: deal.stage_id || ''
    });
    setShowEditDealModal(true);
  };

  const handleUpdateDeal = async () => {
    if (!editingDeal) return;
    try {
      const stage = selectedPipeline?.stages?.find(s => s.id === editingDeal.stage_id);
      const updateData = {
        value: parseFloat(editingDeal.value) || 0,
        monthly_value: parseFloat(editingDeal.monthly_value) || 0,
        assigned_to: editingDeal.assigned_to,
        notes: editingDeal.notes,
        stage_id: editingDeal.stage_id,
        stage_name: stage?.name || editingDeal.stage_name,
        is_won: stage?.is_won_stage || false,
        is_lost: stage?.is_lost_stage || false
      };
      const response = await api.updateDeal(editingDeal.id, updateData);
      setDeals(deals.map(d => d.id === editingDeal.id ? response.data : d));
      setShowEditDealModal(false);
      setEditingDeal(null);
    } catch (error) {
      console.error('Error updating deal:', error);
      alert('Error al actualizar el deal');
    }
  };

  const handleViewDealDetails = async (deal) => {
    // Use the new empresa modal directly
    setSelectedEmpresaIdForModal(deal.empresa_id);
    setShowEmpresaModal(true);
  };

  const handleStartEditValue = (deal) => {
    setEditingDealId(deal.id);
    setEditingDealValue(deal.value?.toString() || '0');
    setEditingDealMonthlyValue(deal.monthly_value?.toString() || '0');
  };

  const handleSaveValue = async (deal) => {
    try {
      const newValue = parseFloat(editingDealValue) || 0;
      const newMonthlyValue = parseFloat(editingDealMonthlyValue) || 0;
      const response = await api.updateDeal(deal.id, { value: newValue, monthly_value: newMonthlyValue });
      setDeals(deals.map(d => d.id === deal.id ? response.data : d));
      // Also update the selected deal if modal is open
      if (selectedDeal && selectedDeal.id === deal.id) {
        setSelectedDeal(response.data);
      }
      setEditingDealId(null);
      setEditingDealValue(null);
      setEditingDealMonthlyValue(null);
    } catch (error) {
      console.error('Error updating deal value:', error);
    }
  };

  const handleCancelEditValue = () => {
    setEditingDealId(null);
    setEditingDealValue(null);
    setEditingDealMonthlyValue(null);
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'Empresa': 'bg-blue-100 text-blue-800',
      'Artista': 'bg-purple-100 text-purple-800',
      'Talento': 'bg-orange-100 text-orange-800'
    };
    return badges[category] || 'bg-gray-100 text-gray-800';
  };

  const handleAddPipeline = async () => {
    try {
      const response = await api.createPipeline({
        ...newPipeline,
        is_active: true
      }, currentUser?.email || 'admin');
      setPipelines([...pipelines, response.data]);
      setShowAddPipelineModal(false);
      setNewPipeline({
        name: '',
        description: '',
        stages: [
          { name: 'Lead', color: '#93C5FD', order: 0, is_won_stage: false, is_lost_stage: false },
          { name: 'Contactado', color: '#3B82F6', order: 1, is_won_stage: false, is_lost_stage: false },
          { name: 'Propuesta', color: '#6B7280', order: 2, is_won_stage: false, is_lost_stage: false },
          { name: 'Cerrado Ganado', color: '#22C55E', order: 3, is_won_stage: true, is_lost_stage: false },
          { name: 'Cerrado Perdido', color: '#EF4444', order: 4, is_won_stage: false, is_lost_stage: true }
        ],
        color: '#D4A843'
      });
    } catch (error) {
      console.error('Error creating pipeline:', error);
    }
  };

  // Stage editing functions
  const openEditStages = () => {
    if (!selectedPipeline) return;
    setEditingStages([...selectedPipeline.stages].map((s, i) => ({ ...s, order: s.order ?? i })));
    setShowEditStagesModal(true);
  };

  const moveStageUp = (index) => {
    if (index === 0) return;
    const newStages = [...editingStages];
    [newStages[index - 1], newStages[index]] = [newStages[index], newStages[index - 1]];
    newStages.forEach((s, i) => s.order = i);
    setEditingStages(newStages);
  };

  const moveStageDown = (index) => {
    if (index === editingStages.length - 1) return;
    const newStages = [...editingStages];
    [newStages[index], newStages[index + 1]] = [newStages[index + 1], newStages[index]];
    newStages.forEach((s, i) => s.order = i);
    setEditingStages(newStages);
  };

  const updateStage = (index, field, value) => {
    const newStages = [...editingStages];
    newStages[index] = { ...newStages[index], [field]: value };
    
    // If setting won/lost, ensure only one stage has each flag
    if (field === 'is_won_stage' && value) {
      newStages.forEach((s, i) => { if (i !== index) s.is_won_stage = false; });
    }
    if (field === 'is_lost_stage' && value) {
      newStages.forEach((s, i) => { if (i !== index) s.is_lost_stage = false; });
    }
    
    setEditingStages(newStages);
  };

  const addNewStage = () => {
    setEditingStages([
      ...editingStages,
      { name: 'Nueva Etapa', color: '#6B7280', order: editingStages.length, is_won_stage: false, is_lost_stage: false }
    ]);
  };

  const removeStage = (index) => {
    if (editingStages.length <= 2) return; // Minimum 2 stages
    const newStages = editingStages.filter((_, i) => i !== index);
    newStages.forEach((s, i) => s.order = i);
    setEditingStages(newStages);
  };

  const saveStages = async () => {
    try {
      await api.updatePipelineStages(selectedPipeline.id, editingStages);
      
      // Update local state
      const updatedPipeline = { ...selectedPipeline, stages: editingStages };
      setPipelines(pipelines.map(p => p.id === selectedPipeline.id ? updatedPipeline : p));
      setSelectedPipeline(updatedPipeline);
      setShowEditStagesModal(false);
    } catch (error) {
      console.error('Error saving stages:', error);
      alert('Error al guardar las etapas');
    }
  };

  const handleDeletePipeline = async (pipelineId) => {
    const pipeline = pipelines.find(p => p.id === pipelineId);
    const pipelineDeals = deals.filter(d => d.pipeline_id === pipelineId);
    
    let confirmMessage = `¿Está seguro de eliminar el embudo "${pipeline?.name}"?`;
    if (pipelineDeals.length > 0) {
      confirmMessage += `\n\nEste embudo tiene ${pipelineDeals.length} deal(s) asociados que también serán afectados.`;
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        await api.deletePipeline(pipelineId);
        const remainingPipelines = pipelines.filter(p => p.id !== pipelineId);
        setPipelines(remainingPipelines);
        
        // Select another pipeline if available
        if (selectedPipeline?.id === pipelineId) {
          setSelectedPipeline(remainingPipelines[0] || null);
        }
        
        // Filter out deals from deleted pipeline
        setDeals(deals.filter(d => d.pipeline_id !== pipelineId));
      } catch (error) {
        console.error('Error deleting pipeline:', error);
        alert('Error al eliminar el embudo');
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="embudos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Embudos de Ventas</h1>
          <p className="text-sm text-gray-500">Arrastra las tarjetas para mover deals entre etapas</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* User Filter */}
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-44">
              <User size={16} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mine">Mis deals</SelectItem>
              {isAdmin && <SelectItem value="all">Todos</SelectItem>}
              {isAdmin && users.map(user => (
                <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowAddPipelineModal(true)} variant="outline">
            <Plus size={18} className="mr-2" />
            Nuevo Embudo
          </Button>
          {selectedPipeline && (
            <>
              <Button onClick={openEditStages} variant="outline">
                <Settings size={18} className="mr-2" />
                Editar Etapas
              </Button>
              {isAdmin && (
                <Button 
                  onClick={() => handleDeletePipeline(selectedPipeline.id)} 
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="delete-pipeline-btn"
                >
                  <Trash2 size={18} className="mr-2" />
                  Eliminar Embudo
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Pipeline Selector */}
      {pipelines.length > 0 && (
        <Tabs 
          value={selectedPipeline?.id || ''} 
          onValueChange={(id) => setSelectedPipeline(pipelines.find(p => p.id === id))}
        >
          <TabsList className="flex-wrap h-auto gap-2">
            {pipelines.map((pipeline) => (
              <TabsTrigger 
                key={pipeline.id} 
                value={pipeline.id}
                className="data-[state=active]:bg-[#D4A843] data-[state=active]:text-white"
              >
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: pipeline.color }}
                />
                {pipeline.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {/* Pipeline Stats */}
      {selectedPipeline && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Mis Deals</p>
              <p className="text-2xl font-bold text-gray-800">{getFilteredDeals().length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Valor en Pipeline</p>
              <p className="text-2xl font-bold text-[#D4A843]">
                ${getFilteredDeals().reduce((sum, d) => sum + (d.value || 0), 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4">
              <p className="text-sm text-green-700 font-medium">💰 Mi Comisión Potencial</p>
              <p className="text-2xl font-bold text-green-600">
                ${getMyPotentialCommission().toLocaleString()}
              </p>
              <p className="text-xs text-green-600">(35% de mis deals activos)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Ganados</p>
              <p className="text-2xl font-bold text-green-600">
                {getFilteredDeals().filter(d => d.is_won).length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Perdidos</p>
              <p className="text-2xl font-bold text-red-600">
                {getFilteredDeals().filter(d => d.is_lost).length}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Kanban Board */}
      {selectedPipeline && (
        <div className="overflow-x-auto pb-4">
          <div className="flex space-x-4 min-w-max" style={{ height: 'calc(100vh - 280px)' }}>
            {getStages().map((stage, stageIndex) => (
              <div 
                key={stage.id || stage.name || stageIndex}
                className="w-72 flex-shrink-0 flex flex-col"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage)}
              >
                <Card className="flex flex-col h-full">
                  {/* Stage Header - Always visible */}
                  <CardHeader className="pb-2 flex-shrink-0" style={{ borderTop: `4px solid ${stage.color}` }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CardTitle className="text-sm font-semibold text-gray-700">
                          {stage.name}
                        </CardTitle>
                        {stage.is_won_stage && <Trophy size={14} className="text-green-500" />}
                        {stage.is_lost_stage && <Ban size={14} className="text-red-500" />}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {getDealsByStage(stage.name).length}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#D4A843] font-medium">
                      ${getStageValue(stage.name).toLocaleString()}
                    </p>
                  </CardHeader>
                  {/* Scrollable deals area */}
                  <CardContent className="space-y-2 flex-1 overflow-y-auto min-h-0 pb-4">
                    {getDealsByStage(stage.name).map((deal) => (
                      <div
                        key={deal.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, deal)}
                        className={`p-3 bg-white border rounded-lg cursor-move hover:shadow-md transition-shadow ${
                          draggedDeal?.id === deal.id ? 'opacity-50' : ''
                        }`}
                        data-testid={`deal-card-${deal.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 text-sm">{deal.empresa_name}</p>
                            {deal.empresa_category && deal.empresa_category !== 'Empresa' && (
                              <Badge className="text-xs mt-1 bg-purple-100 text-purple-700">
                                {deal.empresa_category}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewDealDetails(deal); }}
                              className="p-1 hover:bg-blue-50 rounded"
                              title="Ver detalles de empresa"
                            >
                              <Eye size={14} className="text-blue-500" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleOpenEditDeal(deal); }}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="Editar deal"
                            >
                              <Edit size={14} className="text-gray-500" />
                            </button>
                            <button
                              onClick={() => handleToggleWon(deal)}
                              className={`p-1 rounded ${deal.is_won ? 'bg-green-100' : 'hover:bg-gray-100'}`}
                              title={deal.is_won ? 'Desmarcar como ganado' : 'Marcar como ganado'}
                            >
                              <Check size={14} className={deal.is_won ? 'text-green-600' : 'text-gray-400'} />
                            </button>
                            {canDelete('deals') && (
                              <button
                                onClick={() => handleDeleteDeal(deal.id)}
                                className="p-1 hover:bg-red-50 rounded"
                                data-testid={`delete-deal-${deal.id}`}
                              >
                                <Trash2 size={14} className="text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Editable Value */}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          {editingDealId === deal.id ? (
                            <div className="flex items-center gap-1">
                              <DollarSign size={12} />
                              <Input
                                type="number"
                                value={editingDealValue}
                                onChange={(e) => setEditingDealValue(e.target.value)}
                                className="w-20 h-6 text-xs p-1"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveValue(deal);
                                  if (e.key === 'Escape') handleCancelEditValue();
                                }}
                                autoFocus
                              />
                              <button onClick={() => handleSaveValue(deal)} className="p-0.5 hover:bg-green-100 rounded">
                                <Check size={12} className="text-green-600" />
                              </button>
                              <button onClick={handleCancelEditValue} className="p-0.5 hover:bg-red-100 rounded">
                                <X size={12} className="text-red-500" />
                              </button>
                            </div>
                          ) : (
                            <span 
                              className="flex items-center cursor-pointer hover:text-[#D4A843]"
                              onClick={(e) => { e.stopPropagation(); handleStartEditValue(deal); }}
                              title="Click para editar valor"
                            >
                              <DollarSign size={12} className="mr-1" />
                              ${(deal.value || 0).toLocaleString()}
                              <Edit size={10} className="ml-1 opacity-50" />
                            </span>
                          )}
                          {deal.monthly_value > 0 && (
                            <span className="text-green-600">
                              +${deal.monthly_value}/mes
                            </span>
                          )}
                        </div>
                        <div className="flex items-center mt-2 text-xs text-gray-400">
                          <User size={12} className="mr-1" />
                          {deal.assigned_to}
                        </div>
                      </div>
                    ))}
                    
                    {/* Add Deal Button */}
                    <Button
                      variant="ghost"
                      className="w-full border-2 border-dashed border-gray-200 hover:border-[#D4A843] hover:bg-[#D4A843]/5"
                      onClick={() => {
                        setSelectedStage(stage.name);
                        setShowAddDealModal(true);
                      }}
                    >
                      <Plus size={16} className="mr-2" />
                      Agregar Deal
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No pipelines message */}
      {pipelines.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No hay embudos creados</p>
          <Button onClick={() => setShowAddPipelineModal(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]">
            <Plus size={18} className="mr-2" />
            Crear Primer Embudo
          </Button>
        </div>
      )}

      {/* Add Deal Modal */}
      <Dialog open={showAddDealModal} onOpenChange={setShowAddDealModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Deal a {selectedStage}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empresa *</label>
              <EmpresaSearchSelect
                empresas={empresas.filter(empresa => empresa.assigned_to === currentUser?.name)}
                value={newDeal.empresa_id}
                onChange={(value) => {
                  const empresa = empresas.find(e => e.id === value);
                  setNewDeal({ 
                    ...newDeal, 
                    empresa_id: value || '', 
                    empresa_name: empresa?.name || '',
                    empresa_category: empresa?.category || 'Empresa',
                    assigned_to: empresa?.assigned_to || ''
                  });
                }}
                placeholder="Buscar empresa..."
                data-testid="deal-empresa-search"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor ($)</label>
                <Input
                  type="number"
                  value={newDeal.value}
                  onChange={(e) => setNewDeal({ ...newDeal, value: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor Mensual ($)</label>
                <Input
                  type="number"
                  value={newDeal.monthly_value}
                  onChange={(e) => setNewDeal({ ...newDeal, monthly_value: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asignado a</label>
              <Select value={newDeal.assigned_to} onValueChange={(value) => setNewDeal({ ...newDeal, assigned_to: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar usuario" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea
                value={newDeal.notes}
                onChange={(e) => setNewDeal({ ...newDeal, notes: e.target.value })}
                className="w-full p-2 border rounded-lg text-sm"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDealModal(false)}>Cancelar</Button>
            <Button onClick={handleAddDeal} className="bg-[#D4A843] hover:bg-[#c49a3a]">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Pipeline Modal */}
      <Dialog open={showAddPipelineModal} onOpenChange={setShowAddPipelineModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Embudo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <Input
                value={newPipeline.name}
                onChange={(e) => setNewPipeline({ ...newPipeline, name: e.target.value })}
                placeholder="Nombre del embudo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={newPipeline.description}
                onChange={(e) => setNewPipeline({ ...newPipeline, description: e.target.value })}
                className="w-full p-2 border rounded-lg text-sm"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewPipeline({ ...newPipeline, color })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      newPipeline.color === color ? 'border-gray-800 scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPipelineModal(false)}>Cancelar</Button>
            <Button onClick={handleAddPipeline} className="bg-[#D4A843] hover:bg-[#c49a3a]">Crear Embudo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Stages Modal */}
      <Dialog open={showEditStagesModal} onOpenChange={setShowEditStagesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Etapas - {selectedPipeline?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-500">
              Arrastra las etapas para reordenar. Selecciona el color y marca cuál es la etapa de Ganado y Perdido.
            </p>
            
            <div className="space-y-3">
              {editingStages.map((stage, index) => (
                <div key={stage.id || stage.name || `stage-${index}`} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                  {/* Order buttons */}
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveStageUp(index)}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      disabled={index === 0}
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      onClick={() => moveStageDown(index)}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      disabled={index === editingStages.length - 1}
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  
                  {/* Stage Name */}
                  <Input
                    value={stage.name}
                    onChange={(e) => updateStage(index, 'name', e.target.value)}
                    className="flex-1"
                  />
                  
                  {/* Color Picker */}
                  <div className="relative group">
                    <button
                      className="w-8 h-8 rounded-full border-2 border-gray-300"
                      style={{ backgroundColor: stage.color }}
                    />
                    <div className="absolute hidden group-hover:flex flex-wrap gap-1 p-2 bg-white border rounded-lg shadow-lg z-10 w-48 right-0">
                      {stageColorOptions.map((color) => (
                        <button
                          key={color}
                          onClick={() => updateStage(index, 'color', color)}
                          className={`w-6 h-6 rounded-full border ${stage.color === color ? 'border-black' : 'border-transparent'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Won/Lost Flags */}
                  <button
                    onClick={() => updateStage(index, 'is_won_stage', !stage.is_won_stage)}
                    className={`p-2 rounded ${stage.is_won_stage ? 'bg-green-100 text-green-600' : 'hover:bg-gray-200'}`}
                    title="Marcar como etapa de Ganado"
                  >
                    <Trophy size={16} />
                  </button>
                  <button
                    onClick={() => updateStage(index, 'is_lost_stage', !stage.is_lost_stage)}
                    className={`p-2 rounded ${stage.is_lost_stage ? 'bg-red-100 text-red-600' : 'hover:bg-gray-200'}`}
                    title="Marcar como etapa de Perdido"
                  >
                    <Ban size={16} />
                  </button>
                  
                  {/* Delete */}
                  <button
                    onClick={() => removeStage(index)}
                    className="p-2 hover:bg-red-50 rounded"
                    disabled={editingStages.length <= 2}
                  >
                    <Trash2 size={16} className="text-red-500" />
                  </button>
                </div>
              ))}
            </div>
            
            <Button variant="outline" onClick={addNewStage} className="w-full">
              <Plus size={16} className="mr-2" />
              Agregar Etapa
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditStagesModal(false)}>Cancelar</Button>
            <Button onClick={saveStages} className="bg-[#D4A843] hover:bg-[#c49a3a]">Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deal Detail Modal */}
      <Dialog open={showDealDetailModal} onOpenChange={setShowDealDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center text-xl">
                <Building2 size={24} className="mr-2 text-[#D4A843]" />
                {selectedDeal?.empresa_name}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {selectedDealEmpresa?.category && (
                  <Badge className={getCategoryBadge(selectedDealEmpresa.category)}>
                    {selectedDealEmpresa.category}
                  </Badge>
                )}
                <Badge>{selectedDeal?.stage_name}</Badge>
              </div>
            </div>
          </DialogHeader>
          
          {selectedDeal && (
            <div className="flex-1 overflow-y-auto space-y-4 py-2">
              {/* Deal Value Section - Editable */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gradient-to-r from-[#D4A843]/10 to-[#D4A843]/5 rounded-lg border border-[#D4A843]/20">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Valor del Deal</p>
                  <div className="flex items-center gap-2">
                    <DollarSign size={20} className="text-[#D4A843]" />
                    <Input
                      type="number"
                      value={editingDealId === selectedDeal.id ? editingDealValue : selectedDeal.value || 0}
                      onChange={(e) => {
                        if (editingDealId !== selectedDeal.id) {
                          handleStartEditValue(selectedDeal);
                        }
                        setEditingDealValue(e.target.value);
                      }}
                      onFocus={() => {
                        if (editingDealId !== selectedDeal.id) {
                          handleStartEditValue(selectedDeal);
                        }
                      }}
                      className="text-xl font-bold w-32 h-10"
                      data-testid="deal-value-input"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Valor Mensual</p>
                  <div className="flex items-center gap-2">
                    <DollarSign size={20} className="text-green-600" />
                    <Input
                      type="number"
                      value={editingDealId === selectedDeal.id ? editingDealMonthlyValue : selectedDeal.monthly_value || 0}
                      onChange={(e) => {
                        if (editingDealId !== selectedDeal.id) {
                          handleStartEditValue(selectedDeal);
                        }
                        setEditingDealMonthlyValue(e.target.value);
                      }}
                      onFocus={() => {
                        if (editingDealId !== selectedDeal.id) {
                          handleStartEditValue(selectedDeal);
                        }
                      }}
                      className="text-xl font-bold w-32 h-10"
                      data-testid="deal-monthly-value-input"
                    />
                    <span className="text-sm text-gray-500">/mes</span>
                  </div>
                </div>
                {editingDealId === selectedDeal.id && (
                  <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-[#D4A843]/20">
                    <Button size="sm" variant="outline" onClick={handleCancelEditValue}>
                      <X size={14} className="mr-1" /> Cancelar
                    </Button>
                    <Button size="sm" onClick={() => handleSaveValue(selectedDeal)} className="bg-[#D4A843] hover:bg-[#c49a3a]">
                      <Save size={14} className="mr-1" /> Guardar Valores
                    </Button>
                  </div>
                )}
              </div>

              {/* Empresa Info Tabs */}
              {selectedDealEmpresa && (
                <Tabs value={detailModalTab} onValueChange={setDetailModalTab} className="w-full">
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="info">Información</TabsTrigger>
                    <TabsTrigger value="contacts">Contactos ({selectedDealEmpresa.contacts?.length || 0})</TabsTrigger>
                    <TabsTrigger value="notes">Notas ({selectedDealEmpresa.notes?.length || 0})</TabsTrigger>
                    <TabsTrigger value="tasks">Tareas ({selectedDealEmpresa.tasks?.length || 0})</TabsTrigger>
                  </TabsList>
                  
                  {/* Info Tab */}
                  <TabsContent value="info" className="space-y-3 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      {selectedDealEmpresa.email && (
                        <div className="flex items-center gap-2">
                          <Mail size={16} className="text-gray-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500">Email Principal</p>
                            <p className="text-sm font-medium">{selectedDealEmpresa.email}</p>
                          </div>
                        </div>
                      )}
                      {selectedDealEmpresa.secondary_email && (
                        <div className="flex items-center gap-2">
                          <Mail size={16} className="text-gray-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500">Email Secundario</p>
                            <p className="text-sm font-medium">{selectedDealEmpresa.secondary_email}</p>
                          </div>
                        </div>
                      )}
                      {selectedDealEmpresa.phone && (
                        <div className="flex items-center gap-2">
                          <Phone size={16} className="text-gray-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500">Teléfono</p>
                            <p className="text-sm font-medium">{selectedDealEmpresa.phone}</p>
                          </div>
                        </div>
                      )}
                      {selectedDealEmpresa.website && (
                        <div className="flex items-center gap-2">
                          <Globe size={16} className="text-gray-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500">Website</p>
                            <a href={selectedDealEmpresa.website} target="_blank" rel="noopener noreferrer" 
                               className="text-sm font-medium text-blue-500 hover:underline">
                              {selectedDealEmpresa.website}
                            </a>
                          </div>
                        </div>
                      )}
                      {selectedDealEmpresa.address && (
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className="text-gray-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500">Dirección</p>
                            <p className="text-sm font-medium">{selectedDealEmpresa.address}</p>
                          </div>
                        </div>
                      )}
                      {selectedDealEmpresa.industry && (
                        <div className="flex items-center gap-2">
                          <Briefcase size={16} className="text-gray-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500">Industria</p>
                            <p className="text-sm font-medium">{selectedDealEmpresa.industry}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Asignado a</p>
                          <p className="text-sm font-medium">{selectedDealEmpresa.assigned_to || selectedDeal.assigned_to}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tag size={16} className="text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Etapa Original</p>
                          <p className="text-sm font-medium">{selectedDealEmpresa.stage}</p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  {/* Contacts Tab */}
                  <TabsContent value="contacts" className="mt-4">
                    {selectedDealEmpresa.contacts?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedDealEmpresa.contacts.map((contact, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-800">{contact.name}</p>
                              <p className="text-sm text-gray-500">
                                {contact.position && `${contact.position}`}
                                {contact.position && (contact.email || contact.phone) && ' • '}
                                {contact.email && contact.email}
                                {contact.email && contact.phone && ' • '}
                                {contact.phone && contact.phone}
                              </p>
                            </div>
                            {contact.is_primary && (
                              <Badge className="bg-green-100 text-green-700">Principal</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No hay contactos registrados</p>
                    )}
                  </TabsContent>
                  
                  {/* Notes Tab */}
                  <TabsContent value="notes" className="mt-4">
                    {selectedDealEmpresa.notes?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedDealEmpresa.notes.slice().reverse().map((note, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-700">{note.content}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {note.created_by} - {formatDateTimePR(note.created_at)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No hay notas registradas</p>
                    )}
                  </TabsContent>
                  
                  {/* Tasks Tab */}
                  <TabsContent value="tasks" className="mt-4">
                    {selectedDealEmpresa.tasks?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedDealEmpresa.tasks.map((task, idx) => (
                          <div key={idx} className={`p-3 bg-gray-50 rounded-lg ${task.status === 'Completada' ? 'opacity-60' : ''}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CheckSquare size={16} className={task.status === 'Completada' ? 'text-green-500' : 'text-gray-400'} />
                                <span className={`font-medium ${task.status === 'Completada' ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                                  {task.title}
                                </span>
                              </div>
                              <Badge className={
                                task.priority === 'Alta' ? 'bg-red-100 text-red-700' : 
                                task.priority === 'Media' ? 'bg-yellow-100 text-yellow-700' : 
                                'bg-green-100 text-green-700'
                              }>
                                {task.priority}
                              </Badge>
                            </div>
                            {task.description && (
                              <p className="text-sm text-gray-500 mt-1 ml-6">{task.description}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1 ml-6">
                              {task.due_date && `Vence: ${task.due_date}`} • {task.assigned_to}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No hay tareas asignadas</p>
                    )}
                  </TabsContent>
                </Tabs>
              )}

              {!selectedDealEmpresa && (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-gray-500">Cargando información de la empresa...</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowDealDetailModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empresa Detail Modal from new component */}
      <EmpresaDetailModal
        open={showEmpresaModal}
        onOpenChange={setShowEmpresaModal}
        empresaId={selectedEmpresaIdForModal}
        currentUser={currentUser}
        onEmpresaUpdate={() => loadData()}
        onEmpresaDelete={() => { setShowEmpresaModal(false); loadData(); }}
        initialTab="pipelines"
      />

      {/* Edit Deal Modal */}
      <Dialog open={showEditDealModal} onOpenChange={setShowEditDealModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={18} className="text-[#D4A843]" />
              Editar Deal: {editingDeal?.empresa_name}
            </DialogTitle>
          </DialogHeader>
          {editingDeal && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <DollarSign size={14} className="inline mr-1" />
                    Valor del Deal
                  </label>
                  <Input
                    type="number"
                    value={editingDeal.value || ''}
                    onChange={(e) => setEditingDeal({ ...editingDeal, value: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Mensual
                  </label>
                  <Input
                    type="number"
                    value={editingDeal.monthly_value || ''}
                    onChange={(e) => setEditingDeal({ ...editingDeal, monthly_value: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Etapa</label>
                <Select 
                  value={editingDeal.stage_id || ''} 
                  onValueChange={(value) => {
                    const stage = selectedPipeline?.stages?.find(s => s.id === value);
                    setEditingDeal({ 
                      ...editingDeal, 
                      stage_id: value,
                      stage_name: stage?.name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar etapa" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedPipeline?.stages?.map(stage => (
                      <SelectItem key={stage.id} value={stage.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                          {stage.name}
                          {stage.is_won_stage && <Trophy size={12} className="text-green-500" />}
                          {stage.is_lost_stage && <Ban size={12} className="text-red-500" />}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User size={14} className="inline mr-1" />
                  Asignado a
                </label>
                <Select 
                  value={editingDeal.assigned_to || ''} 
                  onValueChange={(value) => setEditingDeal({ ...editingDeal, assigned_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={editingDeal.notes || ''}
                  onChange={(e) => setEditingDeal({ ...editingDeal, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A843] text-sm"
                  rows={3}
                  placeholder="Notas adicionales sobre este deal..."
                />
              </div>

              <div className="flex items-center gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingDeal.is_won || false}
                    onChange={(e) => setEditingDeal({ 
                      ...editingDeal, 
                      is_won: e.target.checked,
                      is_lost: e.target.checked ? false : editingDeal.is_lost
                    })}
                    className="rounded text-green-500"
                  />
                  <Trophy size={16} className="text-green-500" />
                  <span className="text-sm">Ganado</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingDeal.is_lost || false}
                    onChange={(e) => setEditingDeal({ 
                      ...editingDeal, 
                      is_lost: e.target.checked,
                      is_won: e.target.checked ? false : editingDeal.is_won
                    })}
                    className="rounded text-red-500"
                  />
                  <Ban size={16} className="text-red-500" />
                  <span className="text-sm">Perdido</span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDealModal(false); setEditingDeal(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateDeal} className="bg-[#D4A843] hover:bg-[#c49a3a]">
              <Save size={16} className="mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmbudosPage;
